import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft,
  Building2,
  Globe,
  Upload,
  Search,
  Sparkles,
  Bot,
  Settings,
  CheckSquare,
  AlertTriangle,
  Users,
  Layers,
  Database,
  FileText,
  Target,
  Shield,
  RefreshCw
} from 'lucide-react';

export default function OnboardingGuide() {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <Link
          to="/help/guides"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to User Guides
        </Link>
        <h1 className="text-3xl font-bold mb-2">Website Onboarding Guide</h1>
        <p className="text-muted-foreground">
          Complete walkthrough for onboarding a new website from scratch to AEO-ready content
        </p>
        <div className="flex gap-2 mt-3">
          <Badge>Super Admin</Badge>
          <Badge>Content Manager</Badge>
        </div>
      </div>

      {/* Prerequisites */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Prerequisites
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span><strong>Super Admin access</strong> for sector and site setup</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span><strong>Content Manager access</strong> for feed import and content generation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              <span><strong>Product feed file</strong> (CSV, TSV, or XML format)</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Phase 1 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            Phase 1: Sector Setup
            <Badge variant="outline">Super Admin</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            The sector provides shared research and knowledge that applies to all sites in the same industry.
          </p>

          <div>
            <h4 className="font-medium mb-2">Step 1.1: Check for Existing Sector</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Admin sidebar &rarr; select any site &rarr; check sector assignment
            </p>
            <p className="text-sm">
              If a relevant sector exists (e.g., "Industrial Equipment"), you can use it. Otherwise, create a new one.
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 1.2: Create New Sector (if needed)</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Super Admin &rarr; Sectors
            </p>
            <p className="text-sm">
              Create a sector that represents your industry. For DOGA (industrial motors), an appropriate sector would be "Industrial Motors & Drive Systems".
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 1.3: Run Sector Research</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Research &rarr; Category Perception Research
            </p>
            <p className="text-sm mb-3">
              This is where the magic happens. The system auto-generates all the research data:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm ml-2">
              <li>Select the sector from the global selector</li>
              <li>Enter a category name (e.g., "DC Motors", "Wiper Systems")</li>
              <li>Click the research buttons:</li>
            </ol>
            <div className="mt-3 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 font-medium">Button</th>
                    <th className="text-left p-2 font-medium">What It Generates</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Research Category</td>
                    <td className="p-2">Perceived benefits - bridges technical specs to customer needs</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Research Attribute Values</td>
                    <td className="p-2">Attribute intelligence - explains what spec values mean</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Refresh All</td>
                    <td className="p-2">All 10 characteristics: personas, price tiers, brand tiers, use cases, etc.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm mt-3 text-muted-foreground">
              Repeat for each major category in your catalog (e.g., DC Brushed Motors, DC Brushless Motors, Worm Geared Motors, etc.)
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 1.4: Verify Sector Readiness</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Research &rarr; Category Perception Research &rarr; check readiness indicators
            </p>
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
              <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4 text-green-500" /> Category characteristics researched</p>
              <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4 text-green-500" /> Perceived benefits discovered</p>
              <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4 text-green-500" /> Attribute intelligence generated</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 2 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-green-500" />
            Phase 2: Site Creation
            <Badge variant="outline">Super Admin</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 2.1: Create the Site</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Sites &rarr; Add Site (+ button)
            </p>
            <p className="text-sm mb-2">Fill in the form:</p>
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
              <p><strong>URL:</strong> https://www.doga.es</p>
              <p><strong>Name:</strong> DOGA - Industrial Motors</p>
              <p><strong>Site Type:</strong> paid (or trial/discovery for evaluation)</p>
              <p><strong>Category:</strong> Industrial Manufacturing</p>
              <p><strong>Role Description:</strong> B2B industrial motors and drive systems manufacturer</p>
            </div>
            <p className="text-sm mt-3">Enable features:</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant="secondary">Analytics</Badge>
              <Badge variant="secondary">Chatbot</Badge>
              <Badge variant="secondary">Suggestions</Badge>
              <Badge variant="secondary">Benchmarking</Badge>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 2.2: Assign Sector</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Site Details &rarr; Sector Assignment
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm ml-2">
              <li>Click "Assign Sector"</li>
              <li>Select "Industrial Motors & Drive Systems"</li>
              <li>Save</li>
            </ol>
            <p className="text-sm mt-2 text-muted-foreground">
              This links the site to all the sector research (personas, benefits, category knowledge).
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 2.3: Grant Content Manager Access (Optional)</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Admin &rarr; Site Permissions
            </p>
            <p className="text-sm">
              If someone else will manage content, grant them access via the Site Permissions page.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Phase 3 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-orange-500" />
            Phase 3: Feed Import
            <Badge variant="outline">Content Manager</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 3.1: Prepare Product Feed</h4>
            <p className="text-sm mb-2">Ensure you have a product feed file with:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc">
              <li>Product ID/SKU</li>
              <li>Product name</li>
              <li>Description</li>
              <li>Category</li>
              <li>Technical specifications (voltage, torque, weight, etc.)</li>
              <li>Images (URLs or filenames)</li>
            </ul>
            <p className="text-sm mt-2 text-muted-foreground">
              <strong>Supported formats:</strong> CSV, TSV, XML (Google Shopping format)
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 3.2: Create Feed Import</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Data Sources &rarr; Feed Imports &rarr; Add Feed Import
            </p>
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Step 1 - Source:</p>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li>Enter feed name: "DOGA Product Catalog"</li>
                  <li>Choose source type: Upload File or URL</li>
                </ul>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Step 2 - Analyze:</p>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li>Click "Analyze Feed"</li>
                  <li>Review detected categories and products</li>
                  <li>Select which categories to import</li>
                </ul>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Step 3 - Options:</p>
                <div className="text-sm space-y-1 ml-2 mt-2">
                  <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Auto Normalize - standardize attribute values</p>
                  <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> AI Enrichment - extract attributes from descriptions</p>
                  <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> AEO Generation - generate product content</p>
                  <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Enable Quality Gate - skip products missing critical data</p>
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Step 4 - Schedule:</p>
                <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                  <li><strong>Manual:</strong> Import only when you trigger it</li>
                  <li><strong>Daily/Weekly:</strong> For feeds that update regularly</li>
                </ul>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 3.3: Run Import</h4>
            <p className="text-sm mb-2">Click "Import Now" to start the pipeline.</p>
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-1">Pipeline stages:</p>
              <p className="text-muted-foreground">
                Download &rarr; Parse &rarr; Uniqueness Check &rarr; Normalization &rarr; AI Enrichment &rarr; Vector Embeddings &rarr; AEO Generation &rarr; Complete
              </p>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 3.4: Monitor Progress</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Feed Imports &rarr; click on your import &rarr; View Progress
            </p>
            <p className="text-sm">Watch the phases complete:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-2 mt-2 text-muted-foreground">
              <li><strong>Enrichment</strong> - AI extracts hidden attributes, computes benefit scores</li>
              <li><strong>Embedding</strong> - Generates vectors for semantic search</li>
              <li><strong>AEO Pipeline</strong> - Creates product page content</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Phase 4 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-purple-500" />
            Phase 4: Category Research
            <Badge variant="outline">Content Manager</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            After feed import, run category-specific research for better AEO quality.
          </p>

          <div>
            <h4 className="font-medium mb-2">Step 4.1: Research Product Categories</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Research &rarr; Category Perception Research
            </p>
            <p className="text-sm mb-2">For each category in your feed:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-2">
              <li>Select your site's sector</li>
              <li>Enter category name (e.g., "DC Brushed Motors")</li>
              <li>Click <strong>"Research Category"</strong> &rarr; discovers perceived benefits</li>
              <li>Click <strong>"Research Attribute Values"</strong> &rarr; explains spec meanings</li>
              <li>Review and edit results if needed</li>
            </ol>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 4.2: Run Demand Research</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Research &rarr; My Site Topics (or Demand Intelligence)
            </p>
            <p className="text-sm mb-2">Discover what questions customers are asking:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-2">
              <li>Select your site</li>
              <li>Click "Discover Questions" or "Run Demand Research"</li>
              <li>System aggregates: Google search volumes, People Also Ask, Related searches</li>
            </ol>
            <p className="text-sm mt-2 text-muted-foreground">
              This feeds into FAQ generation.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Phase 5 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Phase 5: AEO Content Generation
            <Badge variant="outline">Content Manager</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 5.1: Review Product AEO</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Content Creation &rarr; Content Settings &rarr; AEO Review
            </p>
            <p className="text-sm mb-2">For each product, the system generates:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc">
              <li>Executive summary</li>
              <li>Best-for badges (persona matches)</li>
              <li>Benefits explained</li>
              <li>Q&A sections</li>
              <li>Specifications table</li>
              <li>Comparison suggestions</li>
            </ul>
            <div className="bg-muted/50 rounded-lg p-3 mt-3">
              <p className="font-medium text-sm mb-1">Review checklist:</p>
              <div className="text-sm space-y-1 text-muted-foreground">
                <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> No hallucinated specifications</p>
                <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Personas correctly matched</p>
                <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Benefits grounded in actual product data</p>
                <p className="flex items-center gap-2"><CheckSquare className="h-4 w-4" /> Q&A answers are accurate</p>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 5.2: Generate Category AEO</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Content Creation &rarr; AI Articles (or Category pages)
            </p>
            <p className="text-sm mb-2">Trigger category-level content generation:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-2">
              <li>Select a category (e.g., "DC Motors")</li>
              <li>Click "Generate Category Content"</li>
              <li>System creates: Category overview, Buyer guide, Use-case segmentation, Top products grid, Category FAQs</li>
            </ol>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 5.3: Approve Content</h4>
            <p className="text-sm">Review generated content and:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc mt-2">
              <li><strong>Approve</strong> good content</li>
              <li><strong>Edit</strong> content that needs tweaks</li>
              <li><strong>Regenerate</strong> content with issues (provide feedback)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Phase 6 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-cyan-500" />
            Phase 6: Discovery Service Verification
            <Badge variant="outline">Super Admin</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 6.1: Check Knowledge Base</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Domain Knowledge &rarr; Knowledge Base
            </p>
            <p className="text-sm">Verify AEO content is indexed: Product summaries, FAQ entries, Use case descriptions, Benefit explanations.</p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 6.2: Test Discovery Queries</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Robot Diagnostics &rarr; Chat Test
            </p>
            <p className="text-sm mb-2">Test natural language queries:</p>
            <div className="border rounded-lg overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 font-medium">Query</th>
                    <th className="text-left p-2 font-medium">Expected Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2">"12V motor for agricultural equipment"</td>
                    <td className="p-2 text-muted-foreground">Relevant motor products with specs</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">"high torque motor under 3kg"</td>
                    <td className="p-2 text-muted-foreground">Filtered results by torque and weight</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">"wiper system for buses"</td>
                    <td className="p-2 text-muted-foreground">Bus-specific wiper products</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">"what voltage options for model 168"</td>
                    <td className="p-2 text-muted-foreground">Specific product Q&A</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 6.3: Enable Chatbot (Optional)</h4>
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Location:</strong> Site Settings &rarr; Features &rarr; Chatbot
            </p>
            <p className="text-sm">
              Enable the chatbot widget for the website to answer product specification questions, application recommendations, and comparison queries.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Phase 7 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-indigo-500" />
            Phase 7: Ongoing Operations
            <Badge variant="outline">Content Manager</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Regular Tasks</h4>

            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">When new feed arrives:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-2 text-muted-foreground">
                  <li>Upload via Feed Imports</li>
                  <li>Run import (incremental - only processes changes)</li>
                  <li>Review new AEO content</li>
                </ol>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Weekly:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-2 text-muted-foreground">
                  <li>Check Demand Intelligence for new questions</li>
                  <li>Review content quality scores</li>
                  <li>Address any flagged content</li>
                </ol>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1">Monthly:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm ml-2 text-muted-foreground">
                  <li>Refresh category research if products changed significantly</li>
                  <li>Review persona match accuracy</li>
                  <li>Check discovery query success rates</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Quick Reference: Role Responsibilities
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Badge>Super Admin</Badge>
              </h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Create/configure sectors</li>
                <li>Run initial sector research</li>
                <li>Create sites and assign sectors</li>
                <li>Grant Content Manager permissions</li>
                <li>Configure discovery service</li>
                <li>Verify knowledge base sync</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Badge>Content Manager</Badge>
              </h4>
              <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
                <li>Upload and configure feed imports</li>
                <li>Monitor import pipeline progress</li>
                <li>Run category-specific research</li>
                <li>Review and approve AEO content</li>
                <li>Generate category-level content</li>
                <li>Handle ongoing feed updates</li>
                <li>Monitor content quality</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Troubleshooting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">Feed import stuck at "Enrichment"</h4>
            <p className="text-sm text-muted-foreground">
              Check if OpenAI API key is configured, verify products have sufficient description text, and check for rate limiting.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">AEO content has wrong specifications</h4>
            <p className="text-sm text-muted-foreground">
              Run "Research Attribute Values" for the category, check if feed data is accurate, then regenerate with feedback.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">Discovery not returning results</h4>
            <p className="text-sm text-muted-foreground">
              Verify knowledge base has entries, check embedding generation completed, and test with simpler queries first.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">Personas don't match products well</h4>
            <p className="text-sm text-muted-foreground">
              Run category research to discover relevant personas, check that benefit scores are computed, and review persona-benefit mappings.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Verification Checklist */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-green-500" />
            Verification Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Sector created/selected',
              'Category research completed (perceived benefits, attribute intelligence)',
              'Site created and sector assigned',
              'Feed imported successfully',
              'All products enriched',
              'AEO content generated',
              'Category AEO generated',
              'Content reviewed and approved',
              'Discovery queries returning relevant results',
              'Chatbot enabled (if needed)'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Checkbox id={`check-${index}`} />
                <label htmlFor={`check-${index}`} className="text-sm cursor-pointer">
                  {item}
                </label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Need more help?{' '}
            <Link to="/help/faq" className="text-primary hover:underline">
              Check our FAQ
            </Link>{' '}
            or{' '}
            <Link to="/my-suggestions" className="text-primary hover:underline">
              submit a question
            </Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
